import { useState, useCallback } from 'react';
import {
  ConditionViewerItem,
  ConditionViewerTerm,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  PanelFooter,
  Stack,
  Text,
  Flex,
  Badge,
  Link,
  Spinner,
  FormattedRelativeTime,
} from '@customerio/pluma-components/react';
import type { ConditionViewerTermProps, PopoverProps, PopoverTriggerProps } from '@customerio/pluma-components/react';
import type { SegmentNode, ConditionNode as ConditionNodeType } from '../../types';
import { parseConditions } from '../../parse';
import { getIconForNode } from '../data-source-icon';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { getApiClientErrorStatus } from '@/services/api/errors';
import { generatePath } from '@/services/router/generate-path';
import { queryEncode } from '@/utils/condition-query-encoding';
import { formatNumberAndPluralize } from '@/utils/number';
import * as styles from './segment.css';

/**
 * The segment a condition names, and the member count its popover shows.
 *
 * The two reads are gated separately because callers need them at different
 * times. A condition row reads the segment as it renders when it has no name
 * of its own, since the name is what its sentence is made of; the popover reads
 * it once opened, for the rest of the record. The count is only ever drawn
 * inside the popover, so it waits for it to open; a list of conditions would
 * otherwise fire a count request per row for numbers nobody has asked to see.
 *
 * `isLoading` is the segment read's alone, and is false whenever that read is
 * switched off, so a caller that needs no lookup never reports itself as
 * waiting for one.
 */
function useSegmentDetails(segmentId: number, workspaceId: number, isSegmentNeeded: boolean, isCountEnabled: boolean) {
  const canFetch = workspaceId > 0 && segmentId > 0;

  const segmentQuery = useHydraUIAPIQuery({
    queryKey: ['getSegmentById', { params: { environment_id: workspaceId, segment_id: segmentId } }],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.get({
        params: { path: { environment_id: String(workspaceId), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: isSegmentNeeded && canFetch, staleTime: 5 * 60 * 1000 },
  });

  const countQuery = useHydraUIAPIQuery({
    queryKey: ['getSegmentCount', { params: { environment_id: workspaceId, segment_id: segmentId } }],
    queryFn: ({ signal }) =>
      hydraUiApi.segments.getCount({
        params: { path: { environment_id: String(workspaceId), segment_id: String(segmentId) } },
        signal,
      }),
    queryConfig: { enabled: isCountEnabled && canFetch, staleTime: 5 * 60 * 1000 },
  });

  return {
    segment: segmentQuery.data?.segment,
    count: peopleCount(countQuery.isLoading, countQuery.data?.count),
    isLoading: segmentQuery.isLoading,
    error: segmentQuery.error,
    lookup: segmentLookup(segmentQuery),
  };
}

/**
 * The segment's size as the card can say it: still being counted, or counted.
 * A count that failed or never answered reads as none, as Ember's
 * `formatNumberAndPluralize` writes an undefined count ("0 people").
 */
type PeopleCount = { kind: 'counting' } | { kind: 'counted'; count: number };

function peopleCount(isLoading: boolean, count: number | undefined): PeopleCount {
  if (count == null && isLoading) return { kind: 'counting' };
  return { kind: 'counted', count: count ?? 0 };
}

/**
 * How far the segment read has got, as the term needs to know it.
 *
 * Only a completed read that answered "no such segment" may call the segment
 * missing. A read that failed for another reason, or that was never switched
 * on, says nothing about whether the segment exists, so those keep a neutral
 * term rather than an accusation.
 */
export type SegmentLookup = 'idle' | 'loading' | 'ready' | 'missing' | 'failed';

const HTTP_NOT_FOUND = 404;

function segmentLookup(query: {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: unknown;
  fetchStatus: string;
}): SegmentLookup {
  if (query.isSuccess) return 'ready';
  if (query.isLoading) return 'loading';
  if (query.isError) return getApiClientErrorStatus(query.error) === HTTP_NOT_FOUND ? 'missing' : 'failed';
  return 'idle';
}

type SegmentPeopleCountProps = {
  segmentId: number;
  workspaceId: number;
  count: PeopleCount;
};

/**
 * The segment's size, as a way to the people in it (`segment-people-count`):
 * the people list filtered to the segment, disabled and reading "… people"
 * while it is counted.
 */
function SegmentPeopleCount({ segmentId, workspaceId, count }: SegmentPeopleCountProps) {
  const href = generatePath(
    '/workspaces/:workspaceId/journeys/people',
    { workspaceId: String(workspaceId) },
    { filters: queryEncode({ and: [{ segment: { id: segmentId } }] }) },
  );
  return (
    <Link href={href} variant="secondary" isDisabled={count.kind === 'counting'}>
      {count.kind === 'counting' ? '… people' : formatNumberAndPluralize(count.count, 'person')}
    </Link>
  );
}

/**
 * What kind of segment the card says this is, in the words the page it
 * replaces uses (`cio-segment-popover-content`): a snapshot is drawn with its
 * conditions like a data-driven one, and only named differently.
 */
function segmentTypeLabel(isManual: boolean, type: string): string {
  if (isManual) return 'Manual segment';
  if (type === 'snapshot') return 'Snapshot segment';
  return 'Data-driven segment';
}

type RenderCondition = (props: { node: ConditionNodeType; depth?: number; workspaceId: number }) => React.ReactNode;

interface SegmentPopoverContentProps {
  segment: NonNullable<ReturnType<typeof useSegmentDetails>['segment']>;
  count: PeopleCount;
  workspaceId: number;
  renderCondition: RenderCondition;
}

function SegmentPopoverContent({ segment, count, workspaceId, renderCondition }: SegmentPopoverContentProps) {
  // The generated segment `type` enum omits the legacy `manual` alias (now surfaced as `static`);
  // widen to preserve the historical manual-segment branch in case the API still returns it.
  const isManual =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    (segment.type as string) === 'manual' || segment.type === 'static';
  const conditions = segment.conditions;
  const segmentUrl = generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
    workspaceId: String(workspaceId),
    segmentId: String(segment.id),
  });

  return (
    <PopoverContent p="0" className={styles.popoverMinWidth}>
      <Panel withSectionBorders className={styles.popoverPanel}>
        <PanelHeader>
          <Stack gap="025">
            <Text size="xs" color="secondary">
              Segment {segment.id}
            </Text>
            <Flex gap="100" alignItems="center">
              <PanelTitle level="5" size="md">
                {segment.name}
              </PanelTitle>
              {segment.archived === true ? <Badge color="red">Archived</Badge> : null}
            </Flex>
          </Stack>
        </PanelHeader>

        <PanelSection className={styles.popoverConditions}>
          <Text size="xs" color="secondary" mb="050" display="inline-block">
            {segmentTypeLabel(isManual, segment.type)}
          </Text>

          {isManual ? (
            <Text size="sm">
              <Text as="span" weight="semibold">
                Created:
              </Text>{' '}
              <FormattedRelativeTime date={segment.created_at} withTooltip />
            </Text>
          ) : conditions != null && typeof conditions === 'object' ? (
            renderCondition({ node: parseConditions(conditions), workspaceId })
          ) : (
            <Text size="sm" color="secondary">
              No conditions defined
            </Text>
          )}
        </PanelSection>

        <PanelFooter>
          <Flex justifyContent="space-between" alignItems="center" w="full">
            <Link href={segmentUrl} variant="secondary" isUnderlined target="_blank">
              View segment
            </Link>
            <SegmentPeopleCount segmentId={segment.id} workspaceId={workspaceId} count={count} />
          </Flex>
        </PanelFooter>
      </Panel>
    </PopoverContent>
  );
}

interface SegmentConditionProps {
  node: SegmentNode;
  workspaceId: number;
  renderCondition: RenderCondition;
}

/**
 * What the term reads while the segment is still being fetched.
 *
 * An ellipsis rather than a name-shaped string: the row is about to say which
 * segment it means, and anything that looks like an answer — an id dressed up
 * as a name — would be read as one and then silently change.
 */
const LOADING_NAME = '…';

/** What the term reads once the segment is known not to be there. */
const MISSING_NAME = 'Missing segment';

/** The words of the term, and the treatment they are drawn in. */
type SegmentTermLabel = {
  text: string;
  /**
   * Overrides for the term's own emphasis, or `undefined` to keep it.
   *
   * Absent rather than `undefined`-valued because Pluma's term applies its
   * defaults before a caller's props, so an explicit `undefined` would
   * overwrite them rather than defer to them.
   */
  props?: Pick<ConditionViewerTermProps, 'color' | 'weight'>;
};

/**
 * What the term says about a segment, given what is known about it so far.
 *
 * A placeholder is not the segment's name, so it does not get the term's
 * emphasis: subtle while the name is on its way, critical once it is known not
 * to be coming.
 */
function segmentTermLabel(name: string | undefined, lookup: SegmentLookup, segmentId: number): SegmentTermLabel {
  if (name != null) return { text: name };
  if (lookup === 'loading') return { text: LOADING_NAME, props: { color: 'secondary', weight: 'regular' } };
  if (lookup === 'missing') return { text: MISSING_NAME, props: { color: 'critical' } };
  // A failed or never-run lookup leaves the segment unnamed, not missing: the
  // id is the one thing the row knows for sure.
  return { text: `Segment ${segmentId}`, props: { color: 'secondary', weight: 'regular' } };
}

type SegmentPopoverBodyProps = {
  segment: ReturnType<typeof useSegmentDetails>['segment'];
  count: PeopleCount;
  isLoading: boolean;
  error: ReturnType<typeof useSegmentDetails>['error'];
  workspaceId: number;
  renderCondition: RenderCondition;
};

/** The hover popover: the segment once it lands, and why it has not otherwise. */
function SegmentPopoverBody({
  segment,
  count,
  isLoading,
  error,
  workspaceId,
  renderCondition,
}: SegmentPopoverBodyProps) {
  if (isLoading) {
    return (
      <PopoverContent p="300">
        <Flex gap="100" alignItems="center" justifyContent="center">
          <Spinner />
          <Text size="sm" color="secondary">
            Loading segment…
          </Text>
        </Flex>
      </PopoverContent>
    );
  }

  if (segment == null) {
    return (
      <PopoverContent p="300">
        <Text size="sm" color="critical">
          {error != null ? 'Failed to load segment' : 'Segment not found'}
        </Text>
      </PopoverContent>
    );
  }

  return (
    <SegmentPopoverContent
      segment={segment}
      count={count}
      workspaceId={workspaceId}
      renderCondition={renderCondition}
    />
  );
}

type SegmentPopoverProps = {
  segmentId: number;
  workspaceId: number;
  renderCondition: RenderCondition;
  /** What the trigger reads, drawn inside a button. */
  children: React.ReactNode;
  /** The trigger's name, for a trigger whose content does not say it. */
  triggerLabel?: string;
  triggerProps?: Pick<PopoverTriggerProps, 'cursor' | 'display' | 'alignItems'>;
  popoverProps?: Pick<PopoverProps, 'floatingPluginOptions' | 'shouldTriggerOnFocus' | 'initialFocus'>;
};

/**
 * A segment's own record on hover: its type, its conditions, its size and a
 * way to it.
 *
 * Nothing is read until the popover first opens, so a list of these costs no
 * request per row, only one per segment somebody actually looks at. The read
 * shares its cache with every other lookup of the same segment.
 */
export function SegmentPopover({
  segmentId,
  workspaceId,
  renderCondition,
  children,
  triggerLabel,
  triggerProps,
  popoverProps,
}: SegmentPopoverProps) {
  const [isPopoverOpened, setIsPopoverOpened] = useState(false);
  const { segment, count, isLoading, error } = useSegmentDetails(
    segmentId,
    workspaceId,
    isPopoverOpened,
    isPopoverOpened,
  );

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setIsPopoverOpened(true);
    }
  }, []);

  return (
    <Popover
      shouldTriggerOnClick={false}
      shouldTriggerOnHover
      placement="bottom"
      onOpenChange={handleOpenChange}
      {...popoverProps}
    >
      <PopoverTrigger aria-label={triggerLabel} {...triggerProps}>
        {children}
      </PopoverTrigger>
      <SegmentPopoverBody
        segment={segment}
        count={count}
        isLoading={isLoading}
        error={error}
        workspaceId={workspaceId}
        renderCondition={renderCondition}
      />
    </Popover>
  );
}

type SegmentTermProps = {
  segmentId: number;
  workspaceId: number;
  renderCondition: RenderCondition;
  /** A name the caller already has, which saves the lookup. */
  segmentName?: string;
};

/**
 * A segment named inside a sentence, with its record on hover: "…" while its name loads, and
 * "Missing segment" once it is gone.
 */
export function SegmentTerm({ segmentId, workspaceId, renderCondition, segmentName }: SegmentTermProps) {
  // A name the caller already has beats any fetch — a parsed condition can
  // carry its segment's name, and a row that already knows what it is about
  // should neither look it up nor blink through a placeholder first. The
  // popover still wants the whole record, so a hover fetches it either way.
  const { segment, lookup } = useSegmentDetails(segmentId, workspaceId, segmentName == null, false);
  const label = segmentTermLabel(segmentName ?? segment?.name, lookup, segmentId);

  return (
    <SegmentPopover
      segmentId={segmentId}
      workspaceId={workspaceId}
      renderCondition={renderCondition}
      triggerProps={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}
    >
      <ConditionViewerTerm {...label.props}>{label.text}</ConditionViewerTerm>
    </SegmentPopover>
  );
}

export function SegmentCondition({ node, workspaceId, renderCondition }: SegmentConditionProps) {
  return (
    <ConditionViewerItem icon={getIconForNode(node)}>
      {node.inverse ? 'not in' : 'in'}{' '}
      <SegmentTerm
        segmentId={node.segmentId}
        workspaceId={workspaceId}
        renderCondition={renderCondition}
        segmentName={node.segmentName}
      />
    </ConditionViewerItem>
  );
}
